import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from './ui/card';
import { Layers, AlertTriangle, Coins } from 'lucide-react';

const money = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`);

export const AssemblyLineBadge = ({ asm }) => {
  const navigate = useNavigate();
  if (!asm) return null;
  return (
    <button
      type="button"
      onClick={() => asm.bom_id && navigate(`/boms/view/${asm.bom_id}`)}
      disabled={!asm.bom_id}
      className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-sans px-1.5 py-0.5 rounded border bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100 disabled:opacity-60"
      title={asm.bom_id ? `Open ${asm.assembly_name} BOM` : 'No BOM yet'}
      data-testid={`bom-line-assembly-badge-${asm.product_type_id}`}
    >
      <Layers className="w-3 h-3" /> Assembly · {asm.bom_id ? `rev ${asm.bom_revision}${asm.bom_status !== 'Released' ? ` (${asm.bom_status})` : ''}` : 'no BOM'}
      {asm.missing_price_skus?.length > 0 && <AlertTriangle className="w-3 h-3 text-amber-600" />}
    </button>
  );
};

const Stat = ({ label, value, hint, testId, strong }) => (
  <div data-testid={testId}>
    <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`mono ${strong ? 'text-xl font-semibold text-slate-900' : 'text-lg text-slate-800'}`}>{value}</div>
    {hint && <div className="text-[11px] text-slate-400">{hint}</div>}
  </div>
);

export const RolledCostCard = ({ bomId }) => {
  const { api } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api().get(`/boms/${bomId}/rolled-cost`).then(r => setD(r.data)).catch(() => setD(null)); }, [api, bomId]);
  if (!d) return null;
  const asmCount = d.lines.filter(l => l.kind === 'assembly').length;
  return (
    <Card className="industrial-card" data-testid="bom-rolled-cost-card">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Coins className="w-4 h-4 text-orange-600" />
          <span className="text-sm font-semibold text-slate-800">Rolled-up cost per unit</span>
          <span className="text-[11px] text-slate-500">· includes {asmCount} assembl{asmCount === 1 ? 'y' : 'ies'} costed level by level</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Materials" value={money(d.material_cost)} hint="all parts, every level" testId="rolled-material-cost" />
          <Stat label="Assembly labour" value={money(d.component_labor_cost)} hint="avg from completed builds" testId="rolled-component-labor" />
          <Stat label="Own labour" value={money(d.own_labor_avg)} hint="avg for this BOM's builds" testId="rolled-own-labor" />
          <Stat label="Total (materials + labour)" value={money(d.total_cost)} testId="rolled-total-cost" strong />
        </div>
        {d.missing_price_skus.length > 0 && (
          <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1 flex gap-1 items-start" data-testid="rolled-missing-prices">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            {d.missing_price_skus.length} part{d.missing_price_skus.length === 1 ? ' has' : 's have'} no purchase price, so the total is understated: <span className="mono">{d.missing_price_skus.slice(0, 8).join(', ')}{d.missing_price_skus.length > 8 ? '…' : ''}</span>
          </p>
        )}
      </CardContent>
    </Card>
  );
};
